// ═══════════════════════════════════════
// SLAs — niveles de servicio (Fase 5.5)
// ═══════════════════════════════════════
// Modelo:
//   slas (ABM):
//     { codigo, nombre, descripcion,
//       aplicaA,                   // 'accionables' | 'mensajes' | 'cualquiera'
//       condiciones,               // filtros opcionales { empresaId?, prioridad?, rolCodigo? }
//       tiempoRespuestaMinutos,    // cuánto tiempo tiene el responsable para reaccionar
//       ventanaAlertaMinutos,      // cuánto antes del vencimiento notificar "por vencer"
//       destinatariosAlertaExtra,  // userIds a notificar además del responsable
//       activo: true, orden }
//
//   slaInstancias (vivas por recurso):
//     { slaId, recursoTipo, recursoId, responsableUserId, empresaId,
//       vencimientoEn,  // Timestamp calculado = creadoEn + tiempoRespuestaMinutos
//       alertadoPorVencer: false,
//       vencido: false,
//       vencidoEn?,
//       resuelto: false,
//       resueltoEn? }
//
// Motor: vive SÓLO en el servidor (`evaluarSLAsCore` en functions/slas.js,
//   scheduled cada 5 min). Desde acá sólo se lo dispara (`evaluarSLAsServer`).
//   El motor gemelo que corría en el navegador se retiró (report
//   LoebiNwNFbQKZP3TYuEO): dos motores sobre las mismas instancias ya habían
//   divergido en silencio, y el del navegador lo podía correr cualquiera
//   desde la consola.
// ═══════════════════════════════════════

import {
  collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, where, Timestamp,
} from 'firebase/firestore'
import { db, auth } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forCreate, forUpdate } from './dbHelpers'

// URL del endpoint Cloud Function (evaluarSLAsManual). El motor real corre cada
// 5 min en evaluarSLAsScheduled — este endpoint es para forzarlo desde la UI.
// Nota: 2nd gen → URL tipo Cloud Run (*.a.run.app), no la clásica cloudfunctions.net.
const EVALUAR_SLAS_URL = 'https://evaluarslasmanual-y6xpmetzva-uc.a.run.app'

// ──────── ABM de SLAs ────────

/** Lista los SLAs activos. */
export async function listarSLAs({ soloActivos = true } = {}) {
  const snap = await getDocs(collection(db, COLLECTIONS.SLAS))
  let list = snap.docs.map(docConId)
  if (soloActivos) list = list.filter(s => s.activo !== false)
  list.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  return list
}

/** Crea o actualiza un SLA (ABM). */
export async function upsertSLA(slaId, datos) {
  const data = {
    codigo: datos.codigo || null,
    nombre: datos.nombre || 'SLA',
    descripcion: datos.descripcion || '',
    aplicaA: datos.aplicaA || 'accionables',
    condiciones: datos.condiciones || {},
    tiempoRespuestaMinutos: Number(datos.tiempoRespuestaMinutos) || 60,
    ventanaAlertaMinutos: Number(datos.ventanaAlertaMinutos) || 15,
    destinatariosAlertaExtra: Array.isArray(datos.destinatariosAlertaExtra) ? datos.destinatariosAlertaExtra : [],
    activo: datos.activo !== false,
    orden: Number(datos.orden) || 100,
  }
  if (!slaId) {
    const payload = forPlatformCreate(data)
    const ref = await addDoc(collection(db, COLLECTIONS.SLAS), payload)
    return { id: ref.id, ...payload }
  }
  const payload = forUpdate(data)
  await updateDoc(doc(db, COLLECTIONS.SLAS, slaId), payload)
  return { id: slaId, ...payload }
}

/** Desactiva (soft delete) un SLA. */
export async function desactivarSLA(slaId) {
  await updateDoc(doc(db, COLLECTIONS.SLAS, slaId), forUpdate({
    activo: false,
  }))
}

// ──────── Instancias vivas ────────

/**
 * Aplica un SLA a un recurso, creando una slaInstancia.
 * Lo llama la creación de casos (`src/core/lib/casos.js`), su único productor.
 */
export async function iniciarSLA({
  slaId, recursoTipo, recursoId, responsableUserId, empresaId = null,
}) {
  if (!slaId || !recursoTipo || !recursoId) throw new Error('slaId, recursoTipo y recursoId requeridos')
  const slaSnap = await (await import('firebase/firestore')).getDoc(doc(db, COLLECTIONS.SLAS, slaId))
  if (!slaSnap.exists()) throw new Error(`SLA ${slaId} no existe`)
  const sla = slaSnap.data()

  const vence = new Date(Date.now() + (sla.tiempoRespuestaMinutos || 60) * 60 * 1000)

  const data = {
    slaId,
    recursoTipo,
    recursoId,
    responsableUserId: responsableUserId || null,
    vencimientoEn: Timestamp.fromDate(vence),
    alertadoPorVencer: false,
    vencido: false,
    vencidoEn: null,
    resuelto: false,
    resueltoEn: null,
  }
  const payload = empresaId ? forCreate(data, empresaId) : forPlatformCreate(data)
  const ref = await addDoc(collection(db, COLLECTIONS.SLA_INSTANCIAS), payload)
  return { id: ref.id, ...payload }
}

/**
 * Marca una instancia como resuelta (p. ej. cuando el responsable aceptó/respondió).
 */
export async function resolverSLA(slaInstanciaId) {
  await updateDoc(doc(db, COLLECTIONS.SLA_INSTANCIAS, slaInstanciaId), forUpdate({
    resuelto: true,
    resueltoEn: serverTimestamp(),
  }))
}

/**
 * Buscar instancias vivas de un recurso.
 * Se usa por ejemplo en `aceptarAccionable` → resolver el SLA asociado.
 */
export async function instanciasVivasDeRecurso(recursoTipo, recursoId) {
  const q = query(
    collection(db, COLLECTIONS.SLA_INSTANCIAS),
    where('recursoTipo', '==', recursoTipo),
    where('recursoId', '==', recursoId),
    where('resuelto', '==', false),
  )
  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

/**
 * Dispara el motor de SLAs en el servidor (Cloud Function).
 * Requiere estar logueado como consultor MilBrands (la function valida el dominio).
 * Devuelve el resumen { evaluadas, porVencer, vencidas, errores }.
 *
 * Esta es la versión recomendada para la UI: la lógica real ya corre cada 5 min
 * en `evaluarSLAsScheduled` (Cloud Scheduler), pero el botón "Evaluar motor" usa
 * este endpoint para forzar una corrida on-demand sin depender de las reglas de
 * Firestore del cliente.
 */
export async function evaluarSLAsServer() {
  const u = auth.currentUser
  if (!u) throw new Error('No hay usuario logueado')
  const idToken = await u.getIdToken()
  const res = await fetch(EVALUAR_SLAS_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error || `HTTP ${res.status}`)
  }
  return data
}

// Exposición en window
if (typeof window !== 'undefined') {
  window.__evaluarSLAsServer = evaluarSLAsServer  // versión Cloud Function
  window.__iniciarSLA = iniciarSLA
  window.__listarSLAs = listarSLAs
  window.__upsertSLA = upsertSLA
}
